<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import Auth from 'dashboard/api/auth';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useSidebar } from 'dashboard/composables/useSidebar';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from 'reka-ui';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const emit = defineEmits(['openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isMobile } = useSidebar();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const handleItemClick = item => {
  if (item.click) {
    item.click();
  } else if (item.link) {
    if (item.nativeLink) {
      window.open(item.link, item.target || '_self');
    } else {
      router.push(item.link);
    }
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger as-child>
      <button
        class="flex gap-2 items-center p-1 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side="isMobile ? 'top' : 'right'"
        align="end"
        :side-offset="4"
        class="w-80 rounded-lg bg-n-solid-1 border border-n-weak p-1.5 shadow-lg z-[9999]"
      >
        <DropdownMenuLabel class="px-2.5 py-2">
          <div class="flex flex-col gap-1.5">
            <span class="text-[15px] font-medium">{{
              currentUser.available_name
            }}</span>
            <span class="text-[13px] text-n-slate-11">{{
              currentUser.email
            }}</span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator class="my-1.5 h-px bg-n-weak" />

        <!-- SidebarProfileMenuStatus as custom content -->
        <SidebarProfileMenuStatus />

        <DropdownMenuSeparator class="my-1.5 h-px bg-n-weak" />

        <template v-for="item in allowedMenuItems" :key="item.label">
          <CustomBrandPolicyWrapper
            :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
          >
            <DropdownMenuItem
              v-if="item.show"
              class="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[15px] cursor-pointer outline-none"
              :class="{
                'hover:bg-n-alpha-2 hover:text-n-slate-12':
                  item.label !== t('SIDEBAR_ITEMS.LOGOUT'),
                'hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400':
                  item.label === t('SIDEBAR_ITEMS.LOGOUT'),
              }"
              @click="handleItemClick(item)"
            >
              <Icon :icon="item.icon" class="size-[18px] flex-shrink-0" />
              <span>{{ item.label }}</span>
            </DropdownMenuItem>
          </CustomBrandPolicyWrapper>
        </template>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
